import {
  AGE_BAND_OPTIONS,
  ANAESTHETIC_PRIORITY_OPTIONS,
  ANAESTHETIC_SETTING_OPTIONS,
  CAPABILITY_LEVELS,
  CAPABILITY_LEVEL_DESCRIPTORS,
  CASE_TYPE_OPTIONS,
  careLevelLabel,
  COMMUNICATION_CATEGORY_OPTIONS,
  COMMUNICATION_OUTCOME_OPTIONS,
  COMMUNICATION_PARTICIPANT_OPTIONS,
  EDT_ACTIVITY_OPTIONS,
  LEARNER_GRADE_OPTIONS,
  REFERRAL_REASON_OPTIONS,
  ageBandCounts,
  anaestheticPriorityCounts,
  anaestheticSettingCounts,
  anaestheticStats,
  capabilityCounts,
  capabilityByCareLevel,
  careLevelCounts,
  caseTypeCounts,
  communicationCategoryCounts,
  complicationList,
  edtActivityCounts,
  edtStats,
  entriesPerMonth,
  formatDuration,
  headlineStats,
  hilloCoverage,
  isCase,
  isCommunication,
  isProcedure,
  labelFor,
  paediatricCount,
  procedureCounts,
  procedureLabel,
  referralReasonCounts,
  reportFilename,
  selectEntries,
  taggedEntryCount,
  teachingOnEntries,
  temporalDistribution,
} from '@iculogbook/shared';
import type {
  AnaestheticCase,
  Case,
  Communication,
  DateRange,
  EdtEntry,
  Entry,
  Procedure,
} from '@iculogbook/shared';

/**
 * The ARCP summary document, as pdfmake content.
 *
 * `PDF_SPEC.md` is a specification, not a sketch, and this file follows it
 * section by section. Two things about it are load-bearing rather than
 * stylistic:
 *
 * - **The GMC number is passed in as an argument and never stored.** It arrives
 *   from React state, is written into the document, and goes out of scope. It
 *   must not reach Dexie, Postgres or a log line, and there is a test asserting
 *   that the string is absent from the output when the toggle is off.
 * - **Every figure comes from the shared aggregations**, the same ones the
 *   dashboard uses. A PDF that disagrees with the screen is hazard H4.
 *
 * Pure: it takes data and returns a document definition. Nothing here touches
 * the DOM, the clock or the database, so the whole document is testable without
 * rendering a byte.
 */

export type PdfOptions = {
  displayName: string;
  /** Present only when the trainee opted in on the options screen. */
  gmcNumber?: string | undefined;
  /**
   * EDT/Teaching entries. Passed separately from `entries` rather than mixed
   * in, because every clinical aggregation reads `ageBand` and
   * `capabilityLevel` and an EDT entry has neither. Defaults to empty, so a
   * caller that has none — and every existing test — needs no change.
   */
  edtEntries?: readonly EdtEntry[] | undefined;
  /**
   * Anaesthetic cases, again passed separately. They are not ICM work and must
   * not reach any ICM aggregation — see the note on `AnaestheticCase`.
   */
  anaestheticCases?: readonly AnaestheticCase[] | undefined;
  range: DateRange;
  includeCurriculum: boolean;
  includeListing: boolean;
  /** Passed in, never read from the clock, so the output is reproducible. */
  generatedAt: Date;
};

const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
const SHORT_DATE = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
});
const TIME = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'short' });

const DASH = '—';

/** `ICULogbook_Sanderson_2025-08-01_to_2026-07-31.pdf` */
export function pdfFilename(displayName: string, range: DateRange): string {
  // Shared with the Excel export on the same screen, so the two files a
  // trainee downloads for one period are named alike.
  return reportFilename(displayName, range, 'pdf');
}

function isoDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${String(date.getFullYear())}-${month}-${day}`;
}

function percent(count: number, total: number): string {
  return total === 0 ? '0%' : `${String(Math.round((count / total) * 100))}%`;
}

/* ------------------------------------------------------------------ tables */

/*
 * The PDF palette, in hex and points.
 *
 * No design tokens here, and no dark theme: pdfmake takes literal values, and
 * this document is always black on white whatever the app is set to. It is
 * printed, put in a portfolio, and photocopied — so every rule is grey, no
 * colour carries meaning on its own, and the only tint is a table band.
 *
 * From §10 of the design handback. Ratios are against white.
 */
const PDF = {
  text: '#111111', //  18.9:1  body
  secondary: '#3a3a3a', //  11.4:1  table headers, sub-rows
  faint: '#666666', //   5.7:1  footer, axis labels
  rule: '#111111', //          section rules, 1pt
  hairline: '#c8c8c8', //   1.7:1  between table rows, 0.5pt
  band: '#f2f0eb', //   1.14:1 table banding — visible on paper, invisible in a photocopy
  accent: '#2f5a8c', //   7.1:1  the mark, and the top capability band
  /* Capability 1–4, pale to strong. One hue, monotonic, so the progression
   * survives being printed in grey. Always accompanied by the level number. */
  capability: ['#e8eef5', '#b9cde1', '#7899bd', '#2f5a8c'],
} as const;

/* Millimetres, in points. pdfmake works in points and the spec is in mm. */
const MM = 2.8346;

/*
 * Newsreader for the title, section heads and stat-tile figures. Registered in
 * pdfmake's virtual filesystem by generate.ts, which must declare a family of
 * this exact name — the two are a pair, and a document naming a font pdfmake
 * has not been given fails at generation rather than at build.
 *
 * Body copy stays Roboto: it carries the tables, and their columns depend on
 * figures that line up.
 */
const HEADING_FONT = 'Newsreader';

function headerRow(cells: string[]): Record<string, unknown>[] {
  return cells.map((text, index) => ({
    text,
    bold: true,
    fontSize: 9,
    color: PDF.secondary,
    characterSpacing: 0.06 * 9,
    // Numeric columns are right-aligned, and their headers follow them.
    alignment: index === 0 ? 'left' : 'right',
  }));
}

/**
 * The one table layout in the document.
 *
 * No vertical rules, ever: columns are held apart by a gutter and by the fact
 * that every number is right-aligned in a tabular figure set. Vertical rules on
 * a table this dense turn it into a grid of boxes and make it harder to read
 * across a row, which is the only direction anyone reads it in.
 *
 * Banding starts at thirteen rows. Below that the hairlines are enough, and a
 * banded five-row table looks like it is trying to be a spreadsheet.
 */
const BAND_FROM_ROWS = 12;

type TableNode = { table: { body: unknown[][] } };

function tableLayout(): Record<string, unknown> {
  return {
    // 0 above the header, 1pt under it, 0.5pt between rows, none at the foot.
    hLineWidth: (index: number, node: TableNode) =>
      index === 0 || index === node.table.body.length ? 0 : index === 1 ? 1 : 0.5,
    vLineWidth: () => 0,
    hLineColor: (index: number) => (index === 1 ? PDF.rule : PDF.hairline),
    paddingLeft: (index: number) => (index === 0 ? 0 : 2 * MM),
    paddingRight: () => 0,
    paddingTop: () => 2.5 * MM * 0.5,
    paddingBottom: () => 2.5 * MM * 0.5,
    fillColor: (rowIndex: number, node: TableNode) => {
      if (rowIndex === 0) return null;
      if (node.table.body.length - 1 <= BAND_FROM_ROWS) return null;
      return rowIndex % 2 === 0 ? PDF.band : null;
    },
  };
}

/**
 * Section 2 — procedures, with variant sub-rows indented beneath their parent.
 *
 * `US-guided` is an em dash where ultrasound does not apply to that procedure,
 * rather than a zero, which would read as "never used it". Success is `n/total`,
 * or an em dash where nothing was recorded either way.
 */
function proceduresTable(entries: readonly Entry[], range: DateRange): Record<string, unknown> {
  const rows = procedureCounts(entries, range);

  const body: unknown[][] = [
    headerRow(['Procedure', 'L1', 'L2', 'L3', 'L4', 'Total', 'US', 'Success']),
  ];

  const totals = [0, 0, 0, 0];

  for (const row of rows) {
    body.push([
      { text: row.label },
      ...row.byLevel.map((count) => ({
        text: count === 0 ? DASH : String(count),
        alignment: 'right',
      })),
      { text: String(row.total), alignment: 'right', bold: true },
      {
        text: row.ultrasoundGuided === null ? DASH : String(row.ultrasoundGuided),
        alignment: 'right',
      },
      {
        text: row.successes === null ? DASH : `${String(row.successes)}/${String(row.total)}`,
        alignment: 'right',
      },
    ]);

    row.byLevel.forEach((count, index) => {
      totals[index] = (totals[index] ?? 0) + count;
    });

    for (const variant of row.variants) {
      body.push([
        { text: `    ${variant.variant}`, italics: true, color: PDF.secondary, fontSize: 8 },
        { text: '', colSpan: 4 },
        {},
        {},
        {},
        { text: String(variant.count), alignment: 'right', fontSize: 8, color: PDF.secondary },
        { text: '' },
        { text: '' },
      ]);
    }
  }

  const grandTotal = rows.reduce((sum, row) => sum + row.total, 0);

  body.push([
    { text: 'Total', bold: true },
    ...totals.map((count) => ({ text: String(count), alignment: 'right', bold: true })),
    { text: String(grandTotal), alignment: 'right', bold: true },
    { text: '' },
    { text: '' },
  ]);

  return {
    table: { headerRows: 1, widths: ['*', 22, 22, 22, 22, 30, 26, 40], body },
    layout: tableLayout(),
    fontSize: 9,
  };
}

/**
 * The complications block. Stated as "none recorded" when there are none, never
 * omitted — an absent block reads as an omission, and a trainee who records
 * complications is demonstrating exactly the honesty a panel wants to see.
 */
function complicationsBlock(entries: readonly Entry[], range: DateRange): unknown[] {
  const list = complicationList(entries, range);
  const performed = procedureCounts(entries, range).reduce((sum, row) => sum + row.total, 0);

  if (list.length === 0) {
    return [
      { text: 'Complications', style: 'h3' },
      { text: 'No complications recorded in this period.', style: 'body' },
    ];
  }

  return [
    { text: 'Complications', style: 'h3' },
    {
      text: `${String(list.length)} of ${String(performed)} procedures (${percent(list.length, performed)}) have a recorded complication.`,
      style: 'body',
    },
    {
      ul: list.map(
        (entry) => `${entry.label}, ${SHORT_DATE.format(entry.occurredAt)} — ${entry.complication}`,
      ),
      style: 'body',
      fontSize: 9,
    },
  ];
}

function caseMix(entries: readonly Entry[], range: DateRange): unknown[] {
  const stats = headlineStats(entries, range);
  const types = caseTypeCounts(entries, range).filter((row) => row.count > 0);
  const reasons = referralReasonCounts(entries, range).filter((row) => row.count > 0);
  const communicationCategories = communicationCategoryCounts(entries, range).filter(
    (row) => row.count > 0,
  );
  const bands = ageBandCounts(entries, range);
  const levels = capabilityCounts(entries, range);
  const paediatric = paediatricCount(entries, range);

  const content: unknown[] = [
    sectionRule(),
    { text: 'Case mix', style: 'h2' },

    { text: 'By case type', style: 'h3' },
    {
      table: {
        headerRows: 1,
        widths: ['*', 40, 40],
        body: [
          headerRow(['Case type', 'Count', '%']),
          ...types.map((row) => [
            labelFor(CASE_TYPE_OPTIONS, row.key),
            { text: String(row.count), alignment: 'right' },
            { text: percent(row.count, stats.cases), alignment: 'right' },
          ]),
        ],
      },
      layout: tableLayout(),
      fontSize: 9,
    },
  ];

  /*
   * Severity, and then capability against severity — the cross-tab that is the
   * point of recording a level of care at all. A case count says how much work
   * a trainee did; this says how much of it was hard, which is what an ARCP
   * panel is reading for.
   *
   * Both are omitted entirely when nobody recorded a level, rather than
   * printed as a table of zeroes: an empty table reads as "this trainee saw no
   * sick patients", which is a far worse claim than saying nothing.
   */
  const severity = careLevelCounts(entries, range);
  const recordedLevels = severity.levels.reduce((sum, row) => sum + row.count, 0);

  if (recordedLevels > 0) {
    content.push(
      { text: 'By level of care', style: 'h3' },
      {
        table: {
          headerRows: 1,
          widths: ['*', 40, 40],
          body: [
            headerRow(['Level of care', 'Count', '%']),
            ...severity.levels.map((row) => [
              row.label,
              { text: String(row.count), alignment: 'right' },
              { text: percent(row.count, recordedLevels), alignment: 'right' },
            ]),
          ],
        },
        layout: tableLayout(),
        fontSize: 9,
      },
    );

    if (severity.notRecorded > 0) {
      content.push({
        text: `Percentages are of the ${String(recordedLevels)} cases with a level of care recorded. A further ${String(severity.notRecorded)} did not record one.`,
        style: 'footnote',
      });
    }

    const cross = capabilityByCareLevel(entries, range);
    const crossRows = cross.rows.filter((row) => row.total > 0);

    if (crossRows.length > 0) {
      content.push(
        { text: 'Capability level by level of care', style: 'h3' },
        {
          table: {
            headerRows: 1,
            widths: ['*', 32, 32, 32, 32, 40],
            body: [
              headerRow(['Level of care', '1', '2', '3', '4', 'Total']),
              ...crossRows.map((row) => [
                row.label,
                ...row.byLevel.map((count) => ({
                  text: count === 0 ? '—' : String(count),
                  alignment: 'right',
                })),
                { text: String(row.total), alignment: 'right' },
              ]),
            ],
          },
          layout: tableLayout(),
          fontSize: 9,
        },
        {
          text:
            cross.excluded === 0
              ? 'Columns are FICM capability levels 1–4. Higher capability on higher levels of care is the progression this table exists to show.'
              : `Columns are FICM capability levels 1–4. ${String(cross.excluded)} cases without a level of care recorded are not counted here.`,
          style: 'footnote',
        },
      );
    }
  }

  if (reasons.length > 0) {
    content.push(
      { text: 'By reason for referral', style: 'h3' },
      {
        table: {
          headerRows: 1,
          widths: ['*', 40, 40],
          body: [
            headerRow(['Reason', 'Count', '%']),
            ...reasons.map((row) => [
              labelFor(REFERRAL_REASON_OPTIONS, row.key),
              { text: String(row.count), alignment: 'right' },
              { text: percent(row.count, stats.cases), alignment: 'right' },
            ]),
          ],
        },
        layout: tableLayout(),
        fontSize: 9,
      },
      {
        text: 'A case may have more than one reason for referral, so these percentages sum to more than 100%.',
        style: 'footnote',
      },
    );
  }

  if (communicationCategories.length > 0) {
    content.push(
      { text: 'By communication category', style: 'h3' },
      {
        table: {
          headerRows: 1,
          widths: ['*', 40, 40],
          body: [
            headerRow(['Category', 'Count', '%']),
            ...communicationCategories.map((row) => [
              row.label,
              { text: String(row.count), alignment: 'right' },
              { text: percent(row.count, stats.communications), alignment: 'right' },
            ]),
          ],
        },
        layout: tableLayout(),
        fontSize: 9,
      },
    );
  }

  content.push(
    { text: 'By patient age band', style: 'h3' },
    {
      table: {
        headerRows: 1,
        widths: ['*', 40, 40],
        body: [
          headerRow(['Age band', 'Count', '%']),
          ...bands.map((row) => [
            labelFor(AGE_BAND_OPTIONS, row.key),
            { text: String(row.count), alignment: 'right' },
            { text: percent(row.count, stats.total), alignment: 'right' },
          ]),
        ],
      },
      layout: tableLayout(),
      fontSize: 9,
    },
    { text: 'By capability level', style: 'h3' },
    {
      table: {
        headerRows: 1,
        widths: ['*', 40, 40],
        body: [
          headerRow(['Capability level', 'Count', '%']),
          ...levels.map((row) => [
            row.label,
            { text: String(row.count), alignment: 'right' },
            { text: percent(row.count, stats.total), alignment: 'right' },
          ]),
        ],
      },
      layout: tableLayout(),
      fontSize: 9,
    },
  );

  if (paediatric > 0) {
    content.push({
      text: `Includes ${String(paediatric)} ${paediatric === 1 ? 'entry' : 'entries'} in patients under 16 years (see HiLLO 13).`,
      style: 'callout',
    });
  }

  return content;
}

/** Section 4 — the out-of-hours 2×2, plus entries per month where meaningful. */
function temporal(entries: readonly Entry[], range: DateRange): unknown[] {
  const distribution = temporalDistribution(entries, range);
  const months = entriesPerMonth(entries, range);
  const stats = headlineStats(entries, range);

  const content: unknown[] = [
    sectionRule(),
    { text: 'Temporal distribution', style: 'h2' },
    {
      table: {
        headerRows: 1,
        widths: ['*', 60, 60, 50],
        body: [
          headerRow(['', 'Day', 'Night', 'Total']),
          [
            { text: 'Weekday', bold: true },
            { text: String(distribution.weekday_day), alignment: 'right' },
            { text: String(distribution.weekday_night), alignment: 'right' },
            { text: String(distribution.weekdayTotal), alignment: 'right' },
          ],
          [
            { text: 'Weekend', bold: true },
            { text: String(distribution.weekend_day), alignment: 'right' },
            { text: String(distribution.weekend_night), alignment: 'right' },
            { text: String(distribution.weekendTotal), alignment: 'right' },
          ],
          [
            { text: 'Total', bold: true },
            { text: String(distribution.dayTotal), alignment: 'right', bold: true },
            { text: String(distribution.nightTotal), alignment: 'right', bold: true },
            { text: String(distribution.total), alignment: 'right', bold: true },
          ],
        ],
      },
      layout: tableLayout(),
      fontSize: 9,
    },
    {
      text: 'Day is 08:00 to 19:59 and night is 20:00 to 07:59, in the trainee’s local time. Classification follows the recorded time of the entry, not the shift it belonged to.',
      style: 'footnote',
    },
    {
      text: `${String(stats.outOfHoursPercent)}% of entries in this period were at night or at a weekend.`,
      style: 'body',
    },
  ];

  // Fewer than ten entries makes a per-month chart meaningless — PDF_SPEC.md.
  if (stats.total >= 10 && months.length > 1) {
    // Kept together, or the heading orphans at the foot of a page and the bars
    // appear overleaf with nothing to say what they are.
    content.push({
      unbreakable: true,
      stack: [{ text: 'Entries per month', style: 'h3' }, monthlyBars(months)],
    });
  }

  return content;
}

/**
 * Entries per month as vector bars.
 *
 * Drawn with pdfmake's canvas rather than an image: it stays sharp when
 * printed, weighs nothing, and needs no chart library in the PDF path. It shows
 * consistency of logging and the gaps where someone was not in ICM, which is
 * the whole reason the spec asks for it.
 */
function monthlyBars(months: readonly { key: string; month: number; count: number }[]): unknown {
  const width = 515;
  const height = 90;
  const peak = Math.max(1, ...months.map((month) => month.count));
  const step = width / months.length;
  const barWidth = Math.max(2, Math.min(18, step * 0.7));

  const canvas: unknown[] = [
    {
      type: 'line',
      x1: 0,
      y1: height,
      x2: width,
      y2: height,
      lineWidth: 0.5,
      lineColor: PDF.hairline,
    },
  ];

  months.forEach((month, index) => {
    const barHeight = (month.count / peak) * (height - 6);
    const x = index * step + (step - barWidth) / 2;

    canvas.push({
      type: 'rect',
      x,
      y: height - barHeight,
      w: barWidth,
      h: barHeight,
      color: PDF.accent,
    });
  });

  // Label every other month when there are many, so the axis stays readable.
  const labelEvery = months.length > 18 ? 3 : months.length > 9 ? 2 : 1;
  const labels = months.map((month, index) =>
    index % labelEvery === 0 ? MONTH.format(new Date(2000, month.month - 1, 1)) : '',
  );

  return {
    stack: [
      { canvas },
      {
        columns: labels.map((label) => ({
          text: label,
          fontSize: 6,
          color: PDF.faint,
          alignment: 'center',
          width: step,
        })),
        columnGap: 0,
      },
    ],
    margin: [0, 4, 0, 8],
  };
}

/**
 * Section 5 — Educational Development Time.
 *
 * The evidence a portfolio needs for the non-clinical HiLLOs, and the part of
 * an ARCP submission that clinical entries cannot speak to at all.
 *
 * Duration is optional on an entry, so the hours column reports what was
 * recorded and the table says how many entries that was. Presenting a total of
 * "62 hours" without saying it came from 11 of 34 entries would understate the
 * work and look like a complete figure; the footnote is what stops that.
 */
function educational(
  entries: readonly Entry[],
  edtEntries: readonly EdtEntry[],
  range: DateRange,
): unknown[] {
  const rows = edtActivityCounts(edtEntries, range);
  const stats = edtStats(edtEntries, range);
  const onEntries = teachingOnEntries(entries, range);

  const body: unknown[][] = [headerRow(['Activity', 'Entries', 'Time recorded'])];

  for (const row of rows) {
    body.push([
      { text: row.label },
      { text: String(row.count), alignment: 'right' },
      {
        text: row.withDuration === 0 ? DASH : formatDuration(row.minutes),
        alignment: 'right',
      },
    ]);
  }

  body.push([
    { text: 'Total', bold: true },
    { text: String(stats.total), alignment: 'right', bold: true },
    {
      text: stats.withDuration === 0 ? DASH : formatDuration(stats.minutes),
      alignment: 'right',
      bold: true,
    },
  ]);

  const content: unknown[] = [
    sectionRule(),
    { text: 'Educational Development Time', style: 'h2' },
  ];

  // A trainee may have recorded teaching on cases without logging any EDT at
  // all. The heading still earns its place; an empty table would not.
  if (stats.total > 0) {
    content.push(
      {
        table: { headerRows: 1, widths: ['*', 60, 80], body },
        layout: tableLayout(),
        fontSize: 9,
      },
      {
        text:
          stats.withDuration === stats.total
            ? 'Time spent was recorded on every entry in this period.'
            : `Time spent is optional and was recorded on ${String(stats.withDuration)} of ${String(stats.total)} entries, so the totals above are a floor rather than a complete figure.`,
        style: 'footnote',
      },
    );
  }

  /*
   * Teaching is the one line a panel looks for by name, and it is buried in an
   * eleven-row table otherwise. It is reported from both places it can be
   * recorded — as its own EDT entry, and against the case it happened in —
   * because a reader wants the total and has no reason to care which field
   * held it.
   */
  if (stats.teachingDelivered > 0 || onEntries.total > 0) {
    const parts: string[] = [];

    if (stats.teachingDelivered > 0) {
      const hours =
        stats.teachingDeliveredMinutes === 0
          ? ''
          : ` (${formatDuration(stats.teachingDeliveredMinutes)} recorded)`;
      parts.push(
        `${String(stats.teachingDelivered)} ${stats.teachingDelivered === 1 ? 'session' : 'sessions'} logged as Educational Development Time${hours}`,
      );
    }

    if (onEntries.total > 0) {
      // A case and a procedure recorded inside it are independent evidence —
      // supervising one line on an admission you otherwise did not teach on is
      // real, and is not implied by the case's own teaching field. Named
      // separately here for the same reason.
      const kinds = [
        onEntries.cases === 0
          ? null
          : `${String(onEntries.cases)} ${onEntries.cases === 1 ? 'case' : 'cases'}`,
        onEntries.procedures === 0
          ? null
          : `${String(onEntries.procedures)} ${onEntries.procedures === 1 ? 'procedure' : 'procedures'}`,
        onEntries.communications === 0
          ? null
          : `${String(onEntries.communications)} communication ${onEntries.communications === 1 ? 'entry' : 'entries'}`,
      ].filter((part): part is string => part !== null);

      const grades = onEntries.byGrade
        .map((row) => `${row.label.toLowerCase()} ×${String(row.count)}`)
        .join(', ');
      parts.push(
        `${kinds.join(' and ')} where the trainee taught or supervised (${grades})`,
      );
    }

    content.push({ text: `Teaching delivered: ${parts.join('; ')}.`, style: 'callout' });

    if (onEntries.byGrade.length > 1) {
      content.push({
        text: 'An entry recording more than one grade counts once against each.',
        style: 'footnote',
      });
    }
  }

  return content;
}

/**
 * Anaesthetic experience.
 *
 * Reported entirely apart from the ICM figures, which is the whole reason
 * anaesthetic cases are a separate record type: "412 cases" on the cover means
 * ICM cases, and a panel reads it that way. This section says what else the
 * trainee did, and says where it came from.
 */
function anaesthetic(cases: readonly AnaestheticCase[], range: DateRange): unknown[] {
  const stats = anaestheticStats(cases, range);
  const settings = anaestheticSettingCounts(cases, range);
  const priorities = anaestheticPriorityCounts(cases, range);

  const content: unknown[] = [
    sectionRule(),
    { text: 'Anaesthetic experience', style: 'h2' },
    {
      table: {
        headerRows: 1,
        widths: ['*', 50, 50, 50, 50, 50],
        body: [
          headerRow(['Setting', 'L1', 'L2', 'L3', 'L4', 'Total']),
          ...settings.map((row) => {
            const inSetting = selectEntries(cases, range).filter(
              (record) => record.setting === row.key,
            );
            const byLevel = CAPABILITY_LEVELS.map(
              (level) => inSetting.filter((record) => record.capabilityLevel === level).length,
            );

            return [
              row.label,
              ...byLevel.map((count) => ({
                text: count === 0 ? DASH : String(count),
                alignment: 'right',
              })),
              { text: String(row.count), alignment: 'right', bold: true },
            ];
          }),
          [
            { text: 'Total', bold: true },
            ...stats.byLevel.map((count) => ({
              text: String(count),
              alignment: 'right',
              bold: true,
            })),
            { text: String(stats.total), alignment: 'right', bold: true },
          ],
        ],
      },
      layout: tableLayout(),
      fontSize: 9,
    },
  ];

  if (priorities.length > 0) {
    content.push(
      { text: 'By NCEPOD priority', style: 'h3' },
      {
        table: {
          headerRows: 1,
          widths: ['*', 40, 40],
          body: [
            headerRow(['Priority', 'Count', '%']),
            ...priorities.map((row) => [
              row.label,
              { text: String(row.count), alignment: 'right' },
              { text: percent(row.count, stats.total), alignment: 'right' },
            ]),
          ],
        },
        layout: tableLayout(),
        fontSize: 9,
      },
      {
        text:
          stats.total === priorities.reduce((sum, row) => sum + row.count, 0)
            ? 'Priority was recorded on every case in this period.'
            : 'Priority is optional and is not recorded on acute pain reviews, so these do not sum to the total above.',
        style: 'footnote',
      },
    );
  }

  content.push({
    text: 'Anaesthetic cases are recorded separately from intensive care work and are excluded from every figure elsewhere in this document, including the total entries, the case mix and the out-of-hours percentage. They are included in curriculum mapping, where HiLLO 10 is Anaesthesia.',
    style: 'disclaimer',
  });

  return content;
}

/** Section 5 — curriculum mapping, with the disclaimer that must accompany it. */
function curriculum(
  entries: readonly Entry[],
  edtEntries: readonly EdtEntry[],
  anaestheticCases: readonly AnaestheticCase[],
  range: DateRange,
): unknown[] {
  /*
   * EDT entries count here and only here among the aggregations. HiLLOs 1 to 4
   * — organisational, patient safety, research, teaching — are the outcomes
   * clinical entries evidence worst, and EDT is where a trainee tags them. A
   * coverage table that left them out would understate exactly the rows it is
   * read for.
   */
  const all = [...entries, ...edtEntries, ...anaestheticCases];
  const coverage = hilloCoverage(all, range);
  const tagged = taggedEntryCount(all, range);
  const total = selectEntries(all, range).length;

  return [
    { text: 'Curriculum mapping', style: 'h2', pageBreak: 'before' },
    {
      table: {
        headerRows: 1,
        widths: ['*', 90, 60],
        body: [
          headerRow(['HiLLO', 'Target level at CCT', 'Entries tagged']),
          ...coverage.map((row) => [
            `${String(row.hillo)} · ${row.shortLabel}`,
            { text: String(row.targetLevel), alignment: 'center' },
            { text: String(row.count), alignment: 'right' },
          ]),
        ],
      },
      layout: tableLayout(),
      fontSize: 9,
    },
    {
      text: `${String(tagged)} of ${String(total)} entries in this period carry at least one HiLLO tag.`,
      style: 'body',
    },
    {
      text: 'HiLLO mapping is optional in ICULogbook and applied at the trainee’s discretion. Untagged entries may still be relevant to any HiLLO. Absence of tagged entries against a HiLLO does not indicate absence of experience.',
      style: 'disclaimer',
    },
  ];
}

/**
 * How many rows the appendix will lay out before it stops.
 *
 * Not an arbitrary limit. pdfmake lays out every cell of a table, and a
 * seven-year logbook is around 6,000 rows and 42,000 cells — enough to take
 * minutes and produce 150 pages that no ARCP panel will read. At roughly 40
 * rows to a page this is about 30 pages of appendix, which is already generous.
 *
 * When it bites, the document says so in plain words and tells the trainee how
 * to get the rest. Silently truncating a record of someone's work would be the
 * worst possible answer.
 */
export const MAX_LISTING_ROWS = 1200;

/**
 * Section 6 — the full listing, reverse-chronological, with nested procedures
 * indented under the case they belong to so one admission reads as one event.
 */
function listing(
  entries: readonly Entry[],
  edtEntries: readonly EdtEntry[],
  anaestheticCases: readonly AnaestheticCase[],
  range: DateRange,
): unknown[] {
  const selected = selectEntries(entries, range);
  const selectedEdt = selectEntries(edtEntries, range);
  const selectedAnaesthetic = selectEntries(anaestheticCases, range);
  const cases = selected.filter(isCase);
  const procedures = selected.filter(isProcedure);
  // Standalone in this phase — not nestable inside a case, unlike a procedure —
  // so every communication is its own row rather than a candidate for `children`.
  const communications = selected.filter(isCommunication);

  const children = new Map<string, Procedure[]>();
  const standalone: Procedure[] = [];

  for (const procedure of procedures) {
    const parentId = procedure.caseId;
    if (parentId === undefined || !cases.some((record) => record.id === parentId)) {
      standalone.push(procedure);
      continue;
    }

    children.set(parentId, [...(children.get(parentId) ?? []), procedure]);
  }

  /*
   * EDT entries sit in the same reverse-chronological run as everything else
   * rather than in a block of their own. The appendix is a record of what the
   * trainee did, in the order they did it, and a week that was three
   * intubations and a teaching session reads as that week.
   */
  const items: (Case | Procedure | Communication | EdtEntry | AnaestheticCase)[] = [
    ...cases,
    ...standalone,
    ...selectedEdt,
    ...selectedAnaesthetic,
    ...communications,
  ].sort((a, b) => b.occurredAt.getTime() - a.occurredAt.getTime());

  const body: unknown[][] = [
    headerRow(['Date', 'Time', 'Type', 'Description', 'Age', 'Lvl', 'Details']),
  ];

  let rows = 0;
  let listed = 0;
  let truncated = false;

  for (const item of items) {
    if (rows >= MAX_LISTING_ROWS) {
      truncated = true;
      break;
    }

    body.push(entryRow(item, false));
    rows += 1;
    listed += 1;

    if (isCase(item)) {
      const nested = (children.get(item.id) ?? []).sort(
        (a, b) => a.occurredAt.getTime() - b.occurredAt.getTime(),
      );
      for (const child of nested) {
        body.push(entryRow(child, true));
        rows += 1;
        listed += 1;
      }
    }
  }

  const total = selected.length + selectedEdt.length + selectedAnaesthetic.length;
  // `selected` already counts communications — they are ICM entries, part of
  // `entries` — so nothing is added to `total` here; only `items` needed them
  // added, since cases/standalone/edt/anaesthetic were built separately.

  return [
    { text: 'Full entry listing', style: 'h2', pageBreak: 'before' },
    {
      text: truncated
        ? `The ${String(listed)} most recent of ${String(total)} entries in this period, newest first. Procedures recorded inside a case are indented beneath it.`
        : 'Every entry in the period, newest first. Procedures recorded inside a case are indented beneath it.',
      style: 'body',
    },
    ...(truncated
      ? [
          {
            text: `This appendix is limited to ${String(MAX_LISTING_ROWS)} rows so the document stays a readable length. The counts in every other section cover all ${String(total)} entries. For a complete listing, generate a summary over a shorter period.`,
            style: 'disclaimer',
          },
        ]
      : []),
    {
      table: { headerRows: 1, widths: [42, 26, 48, '*', 36, 18, 145], body },
      layout: tableLayout(),
      fontSize: 8,
    },
  ];
}

function entryRow(
  entry: Case | Procedure | Communication | EdtEntry | AnaestheticCase,
  nested: boolean,
): unknown[] {
  const shared = [
    { text: SHORT_DATE.format(entry.occurredAt) },
    { text: TIME.format(entry.occurredAt) },
  ];

  if ('setting' in entry) {
    return [
      ...shared,
      { text: 'Anaes' },
      { text: entry.title, bold: true },
      { text: labelFor(AGE_BAND_OPTIONS, entry.ageBand) },
      { text: String(entry.capabilityLevel), alignment: 'center' },
      {
        text: [
          labelFor(ANAESTHETIC_SETTING_OPTIONS, entry.setting),
          entry.priority === undefined
            ? ''
            : labelFor(ANAESTHETIC_PRIORITY_OPTIONS, entry.priority),
          entry.asa === undefined ? '' : `ASA ${String(entry.asa)}`,
        ]
          .filter(Boolean)
          .join(' · '),
      },
    ];
  }

  if ('activity' in entry) {
    // Age and level are em dashes rather than blanks: there is no patient, so
    // there is nothing to record, and a blank cell reads as a missing entry.
    return [
      ...shared,
      { text: 'EDT' },
      { text: entry.title, bold: true },
      { text: DASH, alignment: 'center' },
      { text: DASH, alignment: 'center' },
      {
        text: [
          labelFor(EDT_ACTIVITY_OPTIONS, entry.activity),
          entry.durationMinutes === undefined ? '' : formatDuration(entry.durationMinutes),
          // The span, when there is one. The row's own date column is the
          // start, so this only has to say where the period ended.
          entry.endsAt === undefined ? '' : `to ${SHORT_DATE.format(entry.endsAt)}`,
        ]
          .filter((part) => part !== '')
          .join(' · '),
      },
    ];
  }

  if (isCase(entry)) {
    const reasons = (entry.reasonsForReferral ?? [])
      .map((reason) => labelFor(REFERRAL_REASON_OPTIONS, reason))
      .join(', ');

    const taught = (entry.teachingGrades ?? [])
      .map((grade) => labelFor(LEARNER_GRADE_OPTIONS, grade))
      .join(', ');

    return [
      ...shared,
      { text: 'Case' },
      { text: entry.title, bold: true },
      { text: labelFor(AGE_BAND_OPTIONS, entry.ageBand) },
      { text: String(entry.capabilityLevel), alignment: 'center' },
      {
        text: [
          labelFor(CASE_TYPE_OPTIONS, entry.caseType),
          // `=== undefined`, not `Boolean`: the filter below would drop a
          // level 0 if this produced a falsy value rather than a string.
          entry.careLevel === undefined ? '' : careLevelLabel(entry.careLevel),
          reasons,
          taught === '' ? '' : `Taught: ${taught}`,
        ]
          .filter(Boolean)
          .join(' · '),
      },
    ];
  }

  if (isCommunication(entry)) {
    const withWhom = (entry.withWhom ?? [])
      .map((role) => labelFor(COMMUNICATION_PARTICIPANT_OPTIONS, role))
      .join(', ');

    const taughtOnCommunication = (entry.teachingGrades ?? [])
      .map((grade) => labelFor(LEARNER_GRADE_OPTIONS, grade))
      .join(', ');

    return [
      ...shared,
      { text: 'Comms' },
      { text: labelFor(COMMUNICATION_CATEGORY_OPTIONS, entry.category), bold: true },
      { text: labelFor(AGE_BAND_OPTIONS, entry.ageBand) },
      { text: String(entry.capabilityLevel), alignment: 'center' },
      {
        text: [
          withWhom,
          entry.outcome === undefined
            ? ''
            : labelFor(COMMUNICATION_OUTCOME_OPTIONS, entry.outcome),
          taughtOnCommunication === '' ? '' : `Taught: ${taughtOnCommunication}`,
        ]
          .filter((part) => part !== '')
          .join(' · '),
      },
    ];
  }

  const taughtOnProcedure = (entry.teachingGrades ?? [])
    .map((grade) => labelFor(LEARNER_GRADE_OPTIONS, grade))
    .join(', ');

  const details = [
    entry.variant,
    entry.side === undefined
      ? undefined
      : `${entry.side[0]?.toUpperCase() ?? ''}${entry.side.slice(1)}`,
    entry.ultrasoundGuided === true ? 'US-guided' : undefined,
    (entry.attempts ?? 1) > 1 ? `${String(entry.attempts ?? 1)} attempts` : undefined,
    entry.successful === false ? 'Unsuccessful' : undefined,
    entry.complications === undefined ? undefined : `Complication: ${entry.complications}`,
    taughtOnProcedure === '' ? undefined : `Taught: ${taughtOnProcedure}`,
  ]
    .filter((part): part is string => part !== undefined && part !== '')
    .join(' · ');

  return [
    ...shared,
    { text: nested ? '– Proc' : 'Procedure' },
    {
      text: `${nested ? '   ' : ''}${procedureLabel(entry.procedureKey)}`,
      ...(nested ? { color: PDF.secondary } : {}),
    },
    { text: labelFor(AGE_BAND_OPTIONS, entry.ageBand) },
    { text: String(entry.capabilityLevel), alignment: 'center' },
    { text: details },
  ];
}

/* --------------------------------------------------------------- document */

export function buildDocument(
  entries: readonly Entry[],
  options: PdfOptions,
): Record<string, unknown> {
  const { range, displayName, gmcNumber, generatedAt } = options;
  const edtEntries = options.edtEntries ?? [];
  const anaestheticCases = options.anaestheticCases ?? [];
  const stats = headlineStats(entries, range);
  const edt = edtStats(edtEntries, range);
  const anaes = anaestheticStats(anaestheticCases, range);
  const singleDay = isoDay(range.from) === isoDay(range.to);

  const period = singleDay
    ? DATE.format(range.from)
    : `${DATE.format(range.from)} to ${DATE.format(range.to)} (${String(stats.days)} days)`;

  const content: unknown[] = [
    { text: 'INTENSIVE CARE MEDICINE LOGBOOK SUMMARY', style: 'title' },
    { text: displayName, style: 'name' },
  ];

  // Only ever present because the trainee typed it on the options screen and
  // ticked the box. It is written here and never anywhere else.
  if (gmcNumber !== undefined && gmcNumber.trim() !== '') {
    content.push({ text: `GMC ${gmcNumber.trim()}`, style: 'body' });
  }

  content.push(
    { text: `Period: ${period}`, style: 'body', margin: [0, 2, 0, 10] },
    {
      table: {
        widths: ['*', '*', '*', '*'],
        body: [
          [
            tile(stats.total, 'Total entries'),
            tile(stats.procedures, 'Procedures'),
            tile(stats.cases, 'Cases'),
            tile(stats.distinctProcedureTypes, 'Procedure types'),
          ],
        ],
      },
      layout: 'noBorders',
      margin: [0, 0, 0, 10],
    },
    {
      /*
       * The four tiles above count clinical work, and so does "total entries"
       * everywhere else in this document. EDT is reported as its own line here
       * rather than folded into that total, because redefining the headline
       * figure would put it out of step with the temporal table, the
       * percentages and the appendix — hazard H4, arrived at by tidiness.
       */
      text: [
        `Entries per month (average): ${String(Math.round(stats.averagePerMonth))}`,
        `Out-of-hours activity: ${String(stats.outOfHoursPercent)}% of entries`,
        ...(edt.total === 0
          ? []
          : [
              `Educational Development Time: ${String(edt.total)} ${edt.total === 1 ? 'entry' : 'entries'}${edt.withDuration === 0 ? '' : `, ${formatDuration(edt.minutes)} recorded`}`,
            ]),
        ...(anaes.total === 0
          ? []
          : [
              `Anaesthetic experience: ${String(anaes.total)} ${anaes.total === 1 ? 'case' : 'cases'}, counted separately`,
            ]),
      ].join('\n'),
      style: 'body',
    },
    {
      text: `Generated from ICULogbook (iculogbook.co.uk) on ${DATE_TIME.format(generatedAt)} covering the period ${DATE.format(range.from)} to ${DATE.format(range.to)}. Contains ${String(stats.total)} clinical ${stats.total === 1 ? 'entry' : 'entries'}${edt.total === 0 ? '' : ` and ${String(edt.total)} educational ${edt.total === 1 ? 'entry' : 'entries'}`}, all self-reported by the named trainee. Capability levels follow the FICM ICM Curriculum 2021 four-point capability scale. This record contains no patient-identifiable information.`,
      style: 'provenance',
    },

    { text: 'Procedures', style: 'h2', pageBreak: 'before' },
    proceduresTable(entries, range),
    {
      text: CAPABILITY_LEVELS.map(
        (level) => `L${String(level)}: ${CAPABILITY_LEVEL_DESCRIPTORS[level]}`,
      ).join('\n'),
      style: 'footnote',
    },
    ...complicationsBlock(entries, range),

    { text: '', pageBreak: 'before' },
    ...caseMix(entries, range),
    ...temporal(entries, range),
  );

  // Omitted rather than shown empty: a section headed "Educational Development
  // Time" with nothing under it reads as a gap in the trainee's record, and
  // this app does not tell anyone what they should have done.
  if (edt.total > 0 || teachingOnEntries(entries, range).total > 0) {
    content.push(...educational(entries, edtEntries, range));
  }

  if (anaes.total > 0) {
    content.push(...anaesthetic(anaestheticCases, range));
  }

  if (
    options.includeCurriculum &&
    taggedEntryCount([...entries, ...edtEntries, ...anaestheticCases], range) > 0
  ) {
    content.push(...curriculum(entries, edtEntries, anaestheticCases, range));
  }

  if (options.includeListing && stats.total + edt.total + anaes.total > 0) {
    content.push(...listing(entries, edtEntries, anaestheticCases, range));
  }

  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [57, 45, 57, 50], // 20mm sides, room for the footer.
    info: {
      title: `ICULogbook summary — ${displayName}`,
      author: displayName,
      creator: 'ICULogbook',
    },
    // 10pt on 13pt leading, per §10. lineHeight is a multiple, so 13/10.
    defaultStyle: { font: 'Roboto', fontSize: 10, color: PDF.text, lineHeight: 1.3 },
    content,
    footer: (currentPage: number, pageCount: number) => ({
      text: `ICULogbook · ${displayName} · Generated ${DATE.format(generatedAt)} · Page ${String(currentPage)} of ${String(pageCount)}`,
      alignment: 'center',
      fontSize: 8,
      color: PDF.faint,
      margin: [0, 16, 0, 0],
    }),
    styles: {
      /*
       * Section heads carry a 1pt rule above them and 6mm of space before, so
       * the document reads as a set of sections rather than a continuous
       * column. The rule is drawn by `sectionRule` in the content itself,
       * because pdfmake has no border-top on a text block.
       */
      title: { font: HEADING_FONT, fontSize: 20, bold: true, margin: [0, 0, 0, 8] },
      name: { fontSize: 12, bold: true },
      h2: { font: HEADING_FONT, fontSize: 13, bold: true, margin: [0, 2 * MM, 0, 2 * MM] },
      h3: { fontSize: 10, bold: true, margin: [0, 10, 0, 4] },
      body: { fontSize: 10, margin: [0, 0, 0, 4] },
      footnote: { fontSize: 8, color: PDF.faint, margin: [0, 4, 0, 8] },
      callout: { fontSize: 9, italics: true, margin: [0, 6, 0, 0] },
      disclaimer: { fontSize: 9, italics: true, color: PDF.secondary, margin: [0, 8, 0, 0] },
      /*
       * The provenance statement is the serif's italic. It is the one paragraph
       * on the cover that is about the document rather than about the trainee,
       * and setting it apart is the point — it is also why a face with a real
       * italic was chosen rather than one that would be slanted synthetically.
       */
      provenance: {
        font: HEADING_FONT,
        fontSize: 8,
        italics: true,
        color: PDF.faint,
        margin: [0, 18, 0, 0],
      },
    },
  };
}

/**
 * The 1pt rule that sits above every section head, with 6mm of space before it.
 *
 * A rule rather than more whitespace: a reader with two minutes and a stack of
 * portfolios navigates this document by scanning for the next boundary, and a
 * line is findable at arm's length in a way that a gap is not.
 */
function sectionRule(): Record<string, unknown> {
  return {
    canvas: [
      { type: 'line', x1: 0, y1: 0, x2: 481, y2: 0, lineWidth: 1, lineColor: PDF.rule },
    ],
    margin: [0, 6 * MM, 0, 0],
  };
}

function tile(value: number, label: string): Record<string, unknown> {
  return {
    stack: [
      { text: String(value), font: HEADING_FONT, fontSize: 20, bold: true },
      { text: label, fontSize: 8, color: PDF.faint },
    ],
    margin: [0, 6, 0, 6],
  };
}
